import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import type { Editor } from "@tiptap/core";
import { useToggle } from "@uidotdev/usehooks";
import { init } from "emoji-mart";
import { useTheme } from "next-themes";

import { Button } from "@/components/ui/button/button";
import {
  BW_EMOJI_DOM_ALT,
  BW_EMOJI_PICKER_CATEGORIES,
} from "@/components/ui/editor/core/utils/bw-emoji";
import { BwEmojiSmilingLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

init({ data });

export const EmojiPicker = ({ editor }: { editor: Editor }) => {
  const { theme, systemTheme } = useTheme();
  const [open, toggleOpen] = useToggle(false);
  const handleOnEmojiSelect = (emojiData: any) => {
    if (!editor) return;

    if (emojiData.src) {
      // emoji-mart never sets `alt` on a custom skin, so it is set here rather
      // than read off `emojiData` — the clipboard sanitizer discards any image
      // whose alt is not exactly this, and the turndown rule keys off it too.
      editor.commands.setImage({
        src: emojiData.src,
        alt: BW_EMOJI_DOM_ALT,
      } as any);
    } else {
      editor.commands.insertContent(emojiData.native);
    }
    // Both branches close and refocus: the image branch used to return early,
    // leaving the popover open and the caret lost.
    editor.chain().focus().run();
    toggleOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={toggleOpen}>
      <PopoverTrigger asChild>
        <Button
          kind="ghost"
          variant="neutral"
          size="icon-md"
          data-test="emoji-picker-trigger"
        >
          <BwEmojiSmilingLine />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto bg-transparent shadow-none p-0 border-none"
      >
        <Picker
          emojiSize={20}
          theme={theme === "system" ? systemTheme : theme}
          data={data}
          onEmojiSelect={handleOnEmojiSelect}
          custom={BW_EMOJI_PICKER_CATEGORIES}
        />
      </PopoverContent>
    </Popover>
  );
};
